import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useBluelagoon } from "@bluelagoon";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@shadcn-in-nx/ui/table";
import { Input } from "@shadcn-in-nx/ui/input";
import { Button } from "@shadcn-in-nx/ui/button";
import { Skeleton } from "@shadcn-in-nx/ui/skeleton";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@shadcn-in-nx/ui/select";
import {
    Popover, PopoverContent, PopoverTrigger,
} from "@shadcn-in-nx/ui/popover";
import {
    Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@shadcn-in-nx/ui/command";
import {
    Search, ChevronLeft, ChevronRight, Code2, Archive, User, Building2, Layers, ChevronsUpDown, Check,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@shadcn-in-nx/utils";

export default function AdminWorkflowsPanel() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
    const [selectedCompany, setSelectedCompany] = useState(searchParams.get("company") || "all");
    const [selectedType, setSelectedType] = useState(searchParams.get("type") || "all");
    const [currentPage, setCurrentPage] = useState(Number(searchParams.get("page")) || 1);
    const [pageSize] = useState(10);
    const refTimeout = useRef(null);
    const searchDebounceRef = useRef(null);
    const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);

    const [companySearch, setCompanySearch] = useState("");
    const [companyOpen, setCompanyOpen] = useState(false);
    const companySearchTimeout = useRef(null);
    const companySearchMounted = useRef(false);

    // Sync filters to URL search params
    const updateParams = useCallback((overrides = {}) => {
        const vals = { q: searchQuery, company: selectedCompany, type: selectedType, page: String(currentPage), ...overrides };
        const next = new URLSearchParams();
        Object.entries(vals).forEach(([k, v]) => { if (v && v !== "all" && !(k === "page" && v === "1")) next.set(k, v); });
        setSearchParams(next, { replace: true });
    }, [searchQuery, selectedCompany, selectedType, currentPage, setSearchParams]);

    useEffect(() => { updateParams(); }, [selectedCompany, selectedType, currentPage, debouncedSearch]);

    // Debounce the main search input
    useEffect(() => {
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            updateParams({ q: searchQuery });
        }, 300);
        return () => clearTimeout(searchDebounceRef.current);
    }, [searchQuery]);

    const { call: getAllAdminWorkflows, response: data, isLoading, error } = useBluelagoon({
        method: "getAllAdminWorkflows",
    });

    const { call: getAdminWorkflowFilters, response: filtersData } = useBluelagoon({
        method: "getAdminWorkflowFilters",
        resetResponse: false,
    });

    useEffect(() => { getAdminWorkflowFilters({}); }, []);

    useEffect(() => {
        if (!companySearchMounted.current) { companySearchMounted.current = true; return; }
        if (companySearchTimeout.current) clearTimeout(companySearchTimeout.current);
        companySearchTimeout.current = setTimeout(() => {
            getAdminWorkflowFilters({ companySearch: companySearch || undefined });
        }, 300);
    }, [companySearch]);

    useEffect(() => { fetchWorkflows(); }, [debouncedSearch, selectedCompany, selectedType, currentPage]);

    const fetchWorkflows = () => {
        const params = { limit: pageSize, offset: (currentPage - 1) * pageSize, includeArchived: true };
        if (debouncedSearch?.trim()) params.search = debouncedSearch.trim();
        if (selectedCompany !== "all") params.companyId = selectedCompany;
        if (selectedType !== "all") params.type = selectedType;
        getAllAdminWorkflows(params).catch(console.error);
    };

    const totalPages = Math.ceil((data?.pagination?.total || 0) / pageSize);
    const selectedCompanyName = filtersData?.companies?.find(c => c.id === selectedCompany)?.name;

    if (error) return <div className="p-4 text-red-500">Error: {error.message}</div>;

    return (
        <div className="space-y-4 p-2">
            <h2 className="font-semibold text-xl">Workflows</h2>

            <div className="flex items-center gap-4 flex-wrap">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                        placeholder="Search workflows..."
                        value={searchQuery}
                        onChange={(e) => { setSearchQuery(e.target.value); if (currentPage !== 1) setCurrentPage(1); }}
                        className="pl-10"
                    />
                </div>

                {filtersData?.isSupportRole && (
                    <Popover open={companyOpen} onOpenChange={setCompanyOpen}>
                        <PopoverTrigger asChild>
                            <Button variant="outline" role="combobox" className="w-[200px] justify-between">
                                {selectedCompany === "all" ? "All Companies" : selectedCompanyName || "Select..."}
                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[200px] p-0">
                            <Command shouldFilter={false}>
                                <CommandInput placeholder="Search by name or email..." value={companySearch} onValueChange={setCompanySearch} />
                                <CommandList>
                                    <CommandEmpty>No company found.</CommandEmpty>
                                    <CommandGroup>
                                        <CommandItem value="all" onSelect={() => { setSelectedCompany("all"); setCurrentPage(1); setCompanyOpen(false); }}>
                                            <Check className={cn("mr-2 h-4 w-4", selectedCompany === "all" ? "opacity-100" : "opacity-0")} />
                                            All Companies
                                        </CommandItem>
                                        {filtersData.companies.map((c) => (
                                            <CommandItem key={c.id} value={c.name} onSelect={() => { setSelectedCompany(c.id); setCurrentPage(1); setCompanyOpen(false); }}>
                                                <Check className={cn("mr-2 h-4 w-4", selectedCompany === c.id ? "opacity-100" : "opacity-0")} />
                                                {c.name}
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                    {filtersData?.hasMoreCompanies && (
                                        <div className="p-2 text-xs text-muted-foreground text-center">Type to search more...</div>
                                    )}
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>
                )}

                {filtersData?.types?.length > 0 && (
                    <Select value={selectedType} onValueChange={(v) => { setSelectedType(v); setCurrentPage(1); }}>
                        <SelectTrigger className="w-[150px]"><SelectValue placeholder="All Types" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Types</SelectItem>
                            {filtersData.types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                    </Select>
                )}

                {data?.pagination && (
                    <div className="text-sm text-gray-600">
                        {(currentPage - 1) * pageSize + 1}-{Math.min(currentPage * pageSize, data.pagination.total)} of {data.pagination.total}
                    </div>
                )}
            </div>

            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>Company</TableHead>
                        <TableHead>Workflow</TableHead>
                        <TableHead>Owner</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Steps</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Last Updated</TableHead>
                        <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {isLoading ? (
                        [...Array(5)].map((_, i) => (
                            <TableRow key={i}>
                                <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                                <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                                <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                                <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                                <TableCell><Skeleton className="h-4 w-8" /></TableCell>
                                <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                                <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                                <TableCell><Skeleton className="h-4 w-14" /></TableCell>
                            </TableRow>
                        ))
                    ) : !data?.workflows?.length ? (
                        <TableRow>
                            <TableCell colSpan={8} className="text-center text-gray-500 py-8">
                                No workflows found
                            </TableCell>
                        </TableRow>
                    ) : (
                        data.workflows.map((workflow) => (
                            <TableRow key={workflow.id} className="hover:bg-gray-50">
                                <TableCell className="text-sm text-gray-600">
                                    <span className="flex items-center gap-1">
                                        <Building2 className="w-3 h-3" />
                                        {workflow.app?.company?.name || "Unknown"}
                                    </span>
                                </TableCell>
                                <TableCell className="font-medium">{workflow.name}</TableCell>
                                <TableCell className="text-sm text-gray-600">
                                    <span className="flex items-center gap-1">
                                        <User className="w-3 h-3" />
                                        {workflow.app?.user?.firstName || workflow.app?.user?.email || "Unknown"}
                                    </span>
                                </TableCell>
                                <TableCell>
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700">
                                        {workflow.workflowType || "workflow"}
                                    </span>
                                </TableCell>
                                <TableCell>
                                    <span className="flex items-center gap-1 text-gray-600">
                                        <Layers className="w-3 h-3" />
                                        {workflow._count?.steps || 0}
                                    </span>
                                </TableCell>
                                <TableCell>
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${workflow.archivedAt ? "bg-gray-100 text-gray-600" : "bg-green-100 text-green-700"}`}>
                                        {workflow.archivedAt && <Archive className="w-3 h-3" />}
                                        {workflow.archivedAt ? "Archived" : "Active"}
                                    </span>
                                </TableCell>
                                <TableCell className="text-gray-500 text-sm">
                                    {formatDistanceToNow(new Date(workflow.updatedAt), { addSuffix: true })}
                                </TableCell>
                                <TableCell>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => navigate(`/admin/workflows/${workflow.id}`)}
                                    >
                                        <Code2 className="w-4 h-4 mr-1" /> Open
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>

            {totalPages > 1 && (
                <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">Page {currentPage} of {totalPages}</div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}>
                            <ChevronLeft className="w-4 h-4" /> Previous
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages}>
                            Next <ChevronRight className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
